import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActionMenu, AppButton, IconButton, TextPanel, UiIcon } from "../ui/Primitives";
import RichTextEditor from "../ui/RichTextEditor";
import { copyRichTextToClipboard, hasRichTextContent, richTextToPlainText } from "../../utils/richTextHelpers";

const DEFAULT_CATEGORY = "Allgemein";
const normalizeCategory = (value) => String(value || "").trim() || DEFAULT_CATEGORY;

function BefundbeispieleTab({
  c,
  dark,
  lbl,
  inp,
  labels,
  filterText,
  setFilterText,
  newSectionMod,
  setNewSectionMod,
  newSectionName,
  setNewSectionName,
  befundGroupOptions,
  newBefundGroupName,
  setNewBefundGroupName,
  onCreateBefundGroup,
  onMoveBefundGroup,
  onDeleteBefundGroup,
  confirmCreateSection,
  newTplFormOpen,
  setNewTplFormOpen,
  newTplMod,
  setNewTplMod,
  newTplSection,
  setNewTplSection,
  newTplCategory,
  setNewTplCategory,
  newTplName,
  setNewTplName,
  newTplBefund,
  setNewTplBefund,
  newTplBeurteilung,
  setNewTplBeurteilung,
  confirmSaveNewTemplate,
  isSavingNewTemplate,
  befundbeispiele,
  expandedSections,
  toggleSection,
  filterBefundbeispiele,
  modBadge,
  modColor,
  moveSection,
  confirmDeleteSection,
  loadBefundbeispiel,
  moveTemplate,
  confirmDeleteTemplate,
  onSavePreviewTemplate,
  onRenameBefundSection,
  onDeleteBefundSection,
  showStatus,
  Btn,
}) {
  const [previewTpl, setPreviewTpl] = useState(null);
  const [selectedPreviewText, setSelectedPreviewText] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [previewDraft, setPreviewDraft] = useState(null);
  const [isSavingPreview, setIsSavingPreview] = useState(false);
  const [editingSection, setEditingSection] = useState({ key: "", value: "" });
  const [newCategoryDraftBySection, setNewCategoryDraftBySection] = useState({});
  const [activeCategoryCreateKey, setActiveCategoryCreateKey] = useState("");
  const [isCreateSectionModalOpen, setIsCreateSectionModalOpen] = useState(false);
  const befundPreviewRef = useRef(null);
  const beurteilungPreviewRef = useRef(null);
  const {
    itemLabel = "Befundbeispiel",
    itemLabelPlural = "Befundbeispiele",
    searchPlaceholder = "Befundbeispiele durchsuchen...",
    createTitle = "Neues Befundbeispiel direkt erstellen",
    namePlaceholder = "Befundbeispiel-Name...",
  } = labels || {};

  const categoryOptions = useMemo(() => {
    const options = new Set();
    Object.values(befundbeispiele || {}).forEach((sections) => {
      Object.values(sections || {}).forEach((list) => {
        (list || []).forEach((tpl) => {
          const category = normalizeCategory(tpl?.category);
          if (category !== DEFAULT_CATEGORY) options.add(category);
        });
      });
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b, "de", { numeric: true, sensitivity: "base" }));
  }, [befundbeispiele]);

  const getSectionCategoryKey = (modGroup, sectionName) => `${modGroup}:${sectionName}`;

  const getSectionCategoryOptions = (list, sectionKey) => {
    const values = new Set();
    (list || []).forEach((tpl) => values.add(normalizeCategory(tpl?.category)));
    (newCategoryDraftBySection[`${sectionKey}:saved`] || []).forEach((category) => values.add(normalizeCategory(category)));
    return Array.from(values).sort((a, b) => {
      if (a === DEFAULT_CATEGORY) return -1;
      if (b === DEFAULT_CATEGORY) return 1;
      return a.localeCompare(b, "de", { numeric: true, sensitivity: "base" });
    });
  };

  const addCategoryToSection = (modGroup, sectionName) => {
    const sectionKey = getSectionCategoryKey(modGroup, sectionName);
    const draftKey = `${sectionKey}:draft`;
    const savedKey = `${sectionKey}:saved`;
    const category = normalizeCategory(newCategoryDraftBySection[draftKey]);
    if (category === DEFAULT_CATEGORY) {
      showStatus?.("Bitte Kategorienamen eingeben.", true);
      return;
    }

    setNewCategoryDraftBySection((prev) => {
      const existing = prev[savedKey] || [];
      if (existing.some((entry) => normalizeCategory(entry) === category)) {
        return { ...prev, [draftKey]: "" };
      }

      return {
        ...prev,
        [draftKey]: "",
        [savedKey]: [...existing, category],
      };
    });
    setActiveCategoryCreateKey("");
    toggleSection(`${sectionKey}:category:${category}`);
  };

  const changeTemplateCategory = async (tpl, modGroup, sectionName, nextCategory) => {
    const normalizedCategory = normalizeCategory(nextCategory);
    const updatedTpl = {
      ...tpl,
      mod_group: modGroup,
      section: tpl.section || sectionName,
      category: normalizedCategory === DEFAULT_CATEGORY ? "" : normalizedCategory,
    };
    const ok = await onSavePreviewTemplate?.(updatedTpl);
    if (!ok) return;
    if (previewTpl?.id === tpl.id) {
      setPreviewTpl(updatedTpl);
      setPreviewDraft((prev) => (prev?.id === tpl.id ? { ...prev, category: updatedTpl.category } : prev));
    }
  };

  const getGroupLabel = (modGroup) => {
    if (modGroup === "CT") return "Computertomographie";
    if (modGroup === "MRT") return "Magnetresonanztomographie";
    if (modGroup === "Sonstiges") return "Sonstiges";
    return modGroup;
  };

  const beginRenameSection = (sectionKey, sectionName) => {
    setEditingSection({ key: sectionKey, value: sectionName });
  };

  const cancelRenameSection = () => {
    setEditingSection({ key: "", value: "" });
  };

  const handleRenameSection = async (modGroup, sectionName, list) => {
    const nextName = String(editingSection.value || "").trim();
    if (!nextName) {
      showStatus?.("Bitte Sektion eingeben.", true);
      return;
    }

    if (nextName === sectionName) {
      cancelRenameSection();
      return;
    }

    const ok = await onRenameBefundSection?.(modGroup, sectionName, list, nextName);
    if (!ok) return;
    cancelRenameSection();
  };

  const handleDeleteSection = async (modGroup, sectionName, list) => {
    const templateCount = list.length;
    const confirmed = window.confirm(
      templateCount > 0
        ? `Sektion "${sectionName}" mit ${templateCount} ${itemLabel}${templateCount === 1 ? "" : "e"} löschen?`
        : `Leere Sektion "${sectionName}" löschen?`
    );
    if (!confirmed) return;

    const ok = await onDeleteBefundSection?.(modGroup, sectionName, list);
    if (!ok) return;
    if (editingSection.key) {
      cancelRenameSection();
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await copyRichTextToClipboard(text);
    } catch {
      // Clipboard might be unavailable in some browser contexts.
    }
  };

  const closePreview = useCallback(() => {
    setPreviewTpl(null);
    setSelectedPreviewText("");
    setSelectedSource("");
    setPreviewDraft(null);
    setIsSavingPreview(false);
  }, []);

  const hasUnsavedPreviewChanges = useCallback(() => {
    if (!previewDraft || !previewTpl) return false;
    return (
      String(previewDraft.name || "") !== String(previewTpl.name || "") ||
      String(previewDraft.category || "") !== String(previewTpl.category || "") ||
      String(previewDraft.befund || "") !== String(previewTpl.befund || "") ||
      String(previewDraft.beurteilung || "") !== String(previewTpl.beurteilung || "")
    );
  }, [previewDraft, previewTpl]);

  const confirmDiscardPreviewChanges = useCallback(() => (
    !hasUnsavedPreviewChanges() || window.confirm("Ungespeicherte Änderungen verwerfen?")
  ), [hasUnsavedPreviewChanges]);

  const requestClosePreview = useCallback(() => {
    if (!confirmDiscardPreviewChanges()) return;
    closePreview();
  }, [closePreview, confirmDiscardPreviewChanges]);

  const openPreview = (tpl) => {
    if (previewTpl?.id === tpl?.id) return;
    if (!confirmDiscardPreviewChanges()) return;
    setPreviewTpl(tpl);
    setSelectedPreviewText("");
    setSelectedSource("");
    setPreviewDraft({
      ...tpl,
      name: tpl.name || "",
      category: tpl.category || "",
      befund: tpl.befund || "",
      beurteilung: tpl.beurteilung || "",
    });
  };

  const saveInlineEdit = async () => {
    if (!previewDraft || !onSavePreviewTemplate) return;
    setIsSavingPreview(true);
    const ok = await onSavePreviewTemplate(previewDraft);
    setIsSavingPreview(false);
    if (!ok) return;
    setPreviewTpl(previewDraft);
    setPreviewDraft((prev) => ({ ...prev }));
  };

  const currentPreview = previewDraft || previewTpl;
  const previewIsDirty = hasUnsavedPreviewChanges();
  const workspaceStyle = {
    display: "grid",
    gridTemplateColumns: previewTpl ? "minmax(340px, 0.95fr) minmax(520px, 1.35fr)" : "minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
  };
  const splitPanelStyle = {
    position: "sticky",
    top: 14,
    maxHeight: "calc(100vh - 112px)",
    overflow: "auto",
    background: c.bg,
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    padding: 16,
    boxSizing: "border-box",
  };
  const actionFooterStyle = {
    position: "sticky",
    bottom: -16,
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    margin: "14px -16px -16px",
    padding: "12px 16px",
    flexWrap: "wrap",
    background: c.bg,
    borderTop: `1px solid ${c.border}`,
    borderRadius: "0 0 12px 12px",
  };

  const updateSelectionFrom = (source) => {
    const selected = window.getSelection();
    if (!selected) return;
    const text = selected.toString().trim();
    if (!text) {
      setSelectedPreviewText("");
      setSelectedSource("");
      return;
    }

    const container = source === "befund" ? befundPreviewRef.current : beurteilungPreviewRef.current;
    if (!container) return;
    const anchorNode = selected.anchorNode;
    const focusNode = selected.focusNode;
    const isInside =
      (anchorNode && container.contains(anchorNode)) ||
      (focusNode && container.contains(focusNode));
    if (!isInside) return;

    setSelectedPreviewText(text);
    setSelectedSource(source);
  };

  const applySelectedText = () => {
    if (!previewTpl || !selectedPreviewText || !selectedSource) return;

    if (selectedSource === "befund") {
      loadBefundbeispiel({ ...previewTpl, befund: selectedPreviewText, beurteilung: "" }, "befund");
    } else {
      loadBefundbeispiel({ ...previewTpl, befund: "", beurteilung: selectedPreviewText }, "beurteilung");
    }
    closePreview();
  };

  const confirmCreateSectionModal = async () => {
    const ok = await Promise.resolve(onCreateBefundGroup?.());
    if (ok) {
      setIsCreateSectionModalOpen(false);
    }
  };

  useEffect(() => {
    if (!previewTpl) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") requestClosePreview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewTpl, previewDraft, requestClosePreview]);

  useEffect(() => {
    if (!isCreateSectionModalOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsCreateSectionModalOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isCreateSectionModalOpen]);

  return (
    <div style={{ padding: "18px 22px" }}>
      <div style={{ marginBottom: 16 }}>
        <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder={searchPlaceholder} style={{ ...inp, width: 260 }} />
      </div>

      <div style={{ padding: 14, background: c.bgSecondary, borderRadius: "12px", border: `1px solid ${c.border}`, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setNewTplFormOpen(!newTplFormOpen)}>
          <span style={lbl}>{createTitle}</span>
          <span style={{ fontSize: 16 }}>{newTplFormOpen ? "▾" : "▸"}</span>
        </div>
        {newTplFormOpen && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{ display: "flex", gap: 6 }}>
                {(befundGroupOptions || []).map((m) => {
                  const isActive = newTplMod === m;
                  const bgColor = isActive ? modColor(m).bg : c.bg;
                  const textColor = isActive ? modColor(m).color : c.textSecondary;
                  return (
                    <button key={m} onClick={() => { setNewTplMod(m); setNewSectionMod(m); if (!Object.keys(befundbeispiele[m] || {}).includes(newTplSection)) setNewTplSection(""); }} style={{ padding: "6px 16px", fontSize: 12, fontWeight: 600, borderRadius: "8px", cursor: "pointer", border: `1px solid ${c.border}`, background: bgColor, color: textColor }}>{m}</button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setIsCreateSectionModalOpen(true)}
                  style={{
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: "8px",
                    cursor: "pointer",
                    border: `1px dashed ${c.border}`,
                    background: c.bg,
                    color: c.textSecondary,
                  }}
                >
                  + Modalität/Sektion anlegen (z. B. PET)
                </button>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...lbl }}>Neue Untersektion erstellen oder wählen</label>
              <select
                value={newTplSection || newSectionName}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewTplSection(value);
                  setNewSectionName(value);
                }}
                style={{ ...inp, flex: 1 }}
              >
                <option value="">-- Neue Untersektion erstellen oder wählen --</option>
                {Object.keys(befundbeispiele[newTplMod] || {}).map((sec) => (
                  <option key={sec} value={sec}>{sec}</option>
                ))}
              </select>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewSectionName(value);
                    setNewTplSection(value);
                  }}
                  placeholder="Oder neue Untersektion eingeben (z. B. Thorax)..."
                  style={{ ...inp, flex: 1 }}
                />
                <button type="button" onClick={confirmCreateSection} style={{ background: dark ? "#0d2d1a" : "#f0fff4", color: dark ? "#48bb78" : "#276749", border: `1px solid ${dark ? "#48bb78" : "#9ae6b4"}`, borderRadius: "8px", cursor: "pointer", padding: "6px 12px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}>+ Erstellen</button>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...lbl }}>Kategorie</label>
              <input
                type="text"
                value={newTplCategory}
                onChange={(e) => setNewTplCategory(e.target.value)}
                placeholder="z. B. Kardiomyopathien, Klappen / Vitien, Kurzbausteine..."
                list={`${itemLabel}-category-options`}
                style={{ ...inp }}
              />
              <datalist id={`${itemLabel}-category-options`}>
                {categoryOptions.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
            </div>

            <input type="text" value={newTplName} onChange={(e) => setNewTplName(e.target.value)} placeholder={namePlaceholder} style={{ ...inp }} />
            <RichTextEditor value={newTplBefund} onChange={setNewTplBefund} placeholder="Befund..." minHeight={400} c={c} dark={dark} ariaLabel={`${itemLabel} Befund`} />
            <RichTextEditor value={newTplBeurteilung} onChange={setNewTplBeurteilung} placeholder="Beurteilung (optional)..." minHeight={120} c={c} dark={dark} ariaLabel={`${itemLabel} Beurteilung`} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setNewTplFormOpen(false)} style={{ background: dark ? "#2d1515" : "#fff0f0", color: dark ? "#fc8181" : "#cc3333", border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`, borderRadius: "8px", cursor: "pointer", padding: "6px 12px", fontSize: 12 }}>Abbrechen</button>
              <button type="button" onClick={confirmSaveNewTemplate} disabled={isSavingNewTemplate} style={{ background: dark ? "#0d2d1a" : "#f0fff4", color: dark ? "#48bb78" : "#276749", border: `1px solid ${dark ? "#48bb78" : "#9ae6b4"}`, borderRadius: "8px", cursor: isSavingNewTemplate ? "not-allowed" : "pointer", padding: "6px 12px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, opacity: isSavingNewTemplate ? 0.6 : 1 }}>{isSavingNewTemplate ? "⏳ Speichern..." : "💾 Speichern"}</button>
            </div>
          </div>
        )}
      </div>

      <div style={workspaceStyle}>
        <div style={{ minWidth: 0 }}>
      {(befundGroupOptions || []).map((modGroup, groupIndex, groupList) => {
        const sections = befundbeispiele[modGroup] || {};
        const sectionKeys = Object.keys(sections);
        const groupKey = `group:${modGroup}`;
        const isSearching = !!String(filterText || "").trim();
        const visibleSectionEntries = sectionKeys
          .map((sectionName) => {
            const fullList = sections[sectionName] || [];
            const filtered = filterBefundbeispiele(fullList);
            return { sectionName, fullList, filtered };
          })
          .filter(({ filtered }) => !isSearching || filtered.length > 0);

        if (isSearching && visibleSectionEntries.length === 0) return null;

        const isGroupOpen = isSearching || !!expandedSections[groupKey];
        return (
          <div key={modGroup} style={{ marginBottom: 28 }}>
            <div
              onClick={() => toggleSection(groupKey)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: isGroupOpen ? 10 : 0,
                padding: "10px 12px",
                border: `1px solid ${c.border}`,
                borderRadius: "10px",
                background: c.bgSecondary,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 13 }}>{isGroupOpen ? "▾" : "▸"}</span>
              <span style={modBadge(modGroup)}>{modGroup}</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{getGroupLabel(modGroup)}</span>
              <span style={{ fontSize: 12, color: c.textSecondary, marginLeft: "auto" }}>{Object.values(sections).reduce((a, s) => a + s.length, 0)} {itemLabelPlural} · {sectionKeys.length} Untersektionen</span>
              <IconButton
                c={c}
                dark={dark}
                label="Sektion nach oben"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveBefundGroup?.(modGroup, "up");
                }}
                disabled={groupIndex === 0}
              >
                <UiIcon.ArrowUp />
              </IconButton>
              <IconButton
                c={c}
                dark={dark}
                label="Sektion nach unten"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveBefundGroup?.(modGroup, "down");
                }}
                disabled={groupIndex === groupList.length - 1}
              >
                <UiIcon.ArrowDown />
              </IconButton>
              <IconButton
                c={c}
                dark={dark}
                tone="danger"
                label="Sektion löschen"
                onClick={(e) => {
                  e.stopPropagation();
                  void onDeleteBefundGroup?.(modGroup);
                }}
              >
                <UiIcon.Trash />
              </IconButton>
            </div>
            {isGroupOpen && (visibleSectionEntries.length === 0 ? (
              <div style={{ fontSize: 13, color: c.textSecondary, padding: "8px 0 0 14px" }}>Noch keine Untersektionen für {modGroup}.</div>
            ) : visibleSectionEntries.map(({ sectionName, fullList, filtered }) => {
              const key = `${modGroup}:${sectionName}`;
              const isOpen = isSearching || !!expandedSections[key];
              const sectionCategoryOptions = getSectionCategoryOptions(fullList, key);
              const categoryCount = sectionCategoryOptions.length;
              const categoryGroups = sectionCategoryOptions
                .map((category) => ({
                  category,
                  items: filtered.filter((tpl) => normalizeCategory(tpl.category) === category),
                }))
                .filter((entry) => !isSearching || entry.items.length > 0);
              const isCreatingCategory = activeCategoryCreateKey === key;
              return (
                <div key={sectionName} style={{ marginBottom: 8 }}>
                  <div onClick={() => toggleSection(key)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", background: c.bgSecondary, borderRadius: "8px", cursor: "pointer", border: `1px solid ${c.border}`, marginBottom: isOpen ? 6 : 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13 }}>{isOpen ? "▾" : "▸"}</span>
                      {editingSection.key === key ? (
                        <input
                          type="text"
                          value={editingSection.value}
                          onChange={(e) => setEditingSection((prev) => ({ ...prev, value: e.target.value }))}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") {
                              void handleRenameSection(modGroup, sectionName, sections[sectionName] || []);
                            }
                            if (e.key === "Escape") {
                              cancelRenameSection();
                            }
                          }}
                          placeholder="Sektion"
                          style={{ ...inp, maxWidth: 240, padding: "4px 8px", fontSize: 12 }}
                        />
                      ) : (
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{sectionName}</span>
                      )}
                      <span style={{ fontSize: 11, color: c.textSecondary, marginLeft: "auto" }}>{(sections[sectionName] || []).length} {itemLabelPlural} · {categoryCount} Kategorien</span>
                    </div>
                    {editingSection.key === key ? (
                      <>
                        <IconButton
                          c={c}
                          dark={dark}
                          tone="primary"
                          label="Untersektion speichern"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleRenameSection(modGroup, sectionName, sections[sectionName] || []);
                          }}
                        >
                          💾
                        </IconButton>
                        <IconButton
                          c={c}
                          dark={dark}
                          label="Bearbeiten abbrechen"
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelRenameSection();
                          }}
                        >
                          ↗
                        </IconButton>
                      </>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <IconButton c={c} dark={dark} label="Untersektion nach oben" onClick={(e) => { e.stopPropagation(); moveSection(modGroup, sectionKeys.indexOf(sectionName), "up"); }} disabled={sectionKeys.indexOf(sectionName) === 0}>
                          <UiIcon.ArrowUp />
                        </IconButton>
                        <IconButton c={c} dark={dark} label="Untersektion nach unten" onClick={(e) => { e.stopPropagation(); moveSection(modGroup, sectionKeys.indexOf(sectionName), "down"); }} disabled={sectionKeys.indexOf(sectionName) === sectionKeys.length - 1}>
                          <UiIcon.ArrowDown />
                        </IconButton>
                        <IconButton
                          c={c}
                          dark={dark}
                          label="Untersektion bearbeiten"
                          onClick={(e) => {
                            e.stopPropagation();
                            beginRenameSection(key, sectionName);
                          }}
                        >
                          <UiIcon.Edit />
                        </IconButton>
                        <IconButton
                          c={c}
                          dark={dark}
                          tone="info"
                          label="Kategorie hinzufügen"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveCategoryCreateKey((current) => (current === key ? "" : key));
                          }}
                        >
                          <UiIcon.Add />
                        </IconButton>
                        <IconButton
                          c={c}
                          dark={dark}
                          tone="danger"
                          label="Untersektion löschen"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeleteSection(modGroup, sectionName, sections[sectionName] || []);
                          }}
                        >
                          <UiIcon.Trash />
                        </IconButton>
                      </div>
                    )}
                  </div>
                  {isOpen && (
                    <div style={{ paddingLeft: 14 }}>
                      {isCreatingCategory && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "2px 0 8px" }}>
                          <input
                            type="text"
                            value={newCategoryDraftBySection[`${key}:draft`] || ""}
                            onChange={(e) => setNewCategoryDraftBySection((prev) => ({ ...prev, [`${key}:draft`]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") addCategoryToSection(modGroup, sectionName);
                              if (e.key === "Escape") setActiveCategoryCreateKey("");
                            }}
                            placeholder="Neue Kategorie..."
                            list={`${itemLabel}-category-options`}
                            style={{ ...inp, minWidth: 240, flex: "1 1 240px", padding: "5px 8px", fontSize: 12 }}
                            autoFocus
                          />
                          <AppButton c={c} dark={dark} tone="primary" compact onClick={() => addCategoryToSection(modGroup, sectionName)}>
                            Kategorie erstellen
                          </AppButton>
                          <AppButton c={c} dark={dark} compact onClick={() => setActiveCategoryCreateKey("")}>
                            Abbrechen
                          </AppButton>
                        </div>
                      )}
                      {categoryGroups.length === 0 ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 0" }}>
                          <span style={{ fontSize: 12, color: c.textSecondary }}>{filterText ? "Keine Treffer." : "Noch leer."}</span>
                          {!filterText && (
                            <AppButton
                              c={c}
                              dark={dark}
                              tone="info"
                              compact
                              onClick={() => {
                                setNewTplMod(modGroup);
                                setNewSectionMod(modGroup);
                                setNewTplSection(sectionName);
                                setNewSectionName(sectionName);
                                setNewTplFormOpen(true);
                              }}
                            >
                              + {itemLabel} in dieser Untersektion erstellen
                            </AppButton>
                          )}
                        </div>
                      ) : categoryGroups.map(({ category, items }) => {
                        const categoryKey = `${modGroup}:${sectionName}:category:${category}`;
                        const isCategoryOpen = isSearching || !!expandedSections[categoryKey];

                        return (
                          <div key={category} style={{ marginBottom: 8 }}>
                            <div
                              onClick={() => toggleSection(categoryKey)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "7px 10px",
                                background: c.bgSecondary,
                                border: `1px solid ${c.border}`,
                                borderRadius: 7,
                                cursor: "pointer",
                                marginBottom: isCategoryOpen ? 6 : 0,
                              }}
                            >
                              <span style={{ fontSize: 12 }}>{isCategoryOpen ? "▾" : "▸"}</span>
                              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: c.textSecondary }}>{category}</span>
                              <span style={{ fontSize: 11, color: c.textSecondary, marginLeft: "auto" }}>{items.length}</span>
                            </div>
                            {isCategoryOpen && items.map((tpl) => {
                              const originalIndex = fullList.findIndex((entry) => entry.id === tpl.id);
                              return (
                                <div
                                  key={tpl.id}
                                  onClick={() => openPreview({ ...tpl, mod_group: modGroup, section: tpl.section || sectionName })}
                                  aria-current={previewTpl?.id === tpl.id ? "true" : undefined}
                                  style={{
                                    background: previewTpl?.id === tpl.id ? c.bgSecondary : c.bg,
                                    border: `1px solid ${previewTpl?.id === tpl.id ? (dark ? "#63b3ed" : "#2b6cb0") : c.border}`,
                                    borderRadius: "8px",
                                    padding: "10px 12px",
                                    cursor: "pointer",
                                    marginBottom: 6,
                                    boxShadow: previewTpl?.id === tpl.id ? (dark ? "inset 3px 0 0 #63b3ed" : "inset 3px 0 0 #2b6cb0") : "none",
                                  }}
                                >
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>{tpl.name}</div>
                                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                        {tpl.modalitat && <span style={{ ...modBadge(modGroup), fontSize: 10 }}>{tpl.modalitat}</span>}
                                        {tpl.region && <span style={{ fontSize: 11, color: c.textSecondary, background: c.bgSecondary, padding: "1px 7px", borderRadius: 99 }}>{tpl.region}</span>}
                                        {tpl.geschlecht && <span style={{ fontSize: 11, color: c.textSecondary }}>{tpl.geschlecht === "Mann" ? "♂" : "♀"} {tpl.geschlecht}</span>}
                                      </div>
                                    </div>
                                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                      <select
                                        value={normalizeCategory(tpl.category)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          void changeTemplateCategory(tpl, modGroup, sectionName, e.target.value);
                                        }}
                                        title="Kategorie wechseln"
                                        style={{
                                          ...inp,
                                          width: 180,
                                          padding: "4px 8px",
                                          fontSize: 11,
                                          background: c.bg,
                                        }}
                                      >
                                        {sectionCategoryOptions.map((option) => (
                                          <option key={option} value={option}>{option}</option>
                                        ))}
                                      </select>
                                      <AppButton
                                        c={c}
                                        dark={dark}
                                        tone="primary"
                                        compact
                                        title={`${itemLabel} direkt übernehmen`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          loadBefundbeispiel({ ...tpl, mod_group: modGroup, section: tpl.section || sectionName }, "all");
                                        }}
                                        disabled={!hasRichTextContent(tpl.befund) && !hasRichTextContent(tpl.beurteilung)}
                                      >
                                        Übernehmen
                                      </AppButton>
                                      <ActionMenu c={c} dark={dark} label={`Aktionen für ${tpl.name}`}>
                                        <IconButton c={c} dark={dark} label={`${itemLabel} nach oben`} onClick={() => moveTemplate(modGroup, sectionName, originalIndex, "up")} disabled={originalIndex <= 0}><UiIcon.ArrowUp /></IconButton>
                                        <IconButton c={c} dark={dark} label={`${itemLabel} nach unten`} onClick={() => moveTemplate(modGroup, sectionName, originalIndex, "down")} disabled={originalIndex === -1 || originalIndex >= fullList.length - 1}><UiIcon.ArrowDown /></IconButton>
                                        <IconButton c={c} dark={dark} tone="danger" label={`${itemLabel} löschen`} onClick={() => confirmDeleteTemplate(tpl.id, modGroup, sectionName)}><UiIcon.Trash /></IconButton>
                                      </ActionMenu>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 6, fontFamily: "monospace", lineHeight: 1.6, whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{richTextToPlainText(tpl.befund)}</div>
                                  {hasRichTextContent(tpl.beurteilung) && <div style={{ fontSize: 11, color: c.textTertiary, marginTop: 4, fontStyle: "italic", whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>↳ {richTextToPlainText(tpl.beurteilung)}</div>}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }))}
          </div>
        );
      })}
        </div>

      {previewTpl && (
        <aside style={splitPanelStyle} aria-label={`${itemLabel} bearbeiten`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ flex: "1 1 auto", minWidth: 0, maxWidth: 760 }}>
                <input
                  type="text"
                  value={previewDraft?.name || ""}
                  onChange={(e) => setPreviewDraft((prev) => ({ ...prev, name: e.target.value }))}
                  style={{ ...inp, width: "100%", fontSize: 14, fontWeight: 600 }}
                />
                <input
                  type="text"
                  value={previewDraft?.category || ""}
                  onChange={(e) => setPreviewDraft((prev) => ({ ...prev, category: e.target.value }))}
                  placeholder="Kategorie"
                  list={`${itemLabel}-category-options`}
                  style={{ ...inp, width: "100%", marginTop: 8, fontSize: 12 }}
                />
                <div style={{ marginTop: 4, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ ...modBadge(currentPreview?.modalitat || currentPreview?.mod_group || "MRT"), fontSize: 10 }}>{currentPreview?.modalitat || currentPreview?.mod_group || ""}</span>
                  <span style={{ fontSize: 11, color: c.textSecondary }}>{normalizeCategory(currentPreview?.category)}</span>
                  {currentPreview?.region && <span style={{ fontSize: 11, color: c.textSecondary }}>{currentPreview.region}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {previewIsDirty && <span style={{ fontSize: 11, color: dark ? "#f6ad55" : "#b45309", fontWeight: 700 }}>Nicht gespeichert</span>}
                <IconButton c={c} dark={dark} tone={previewIsDirty ? "primary" : "neutral"} onClick={saveInlineEdit} disabled={isSavingPreview || !previewIsDirty} label="Speichern">💾</IconButton>
                <IconButton c={c} dark={dark} onClick={requestClosePreview} label="Editor schließen">↗</IconButton>
              </div>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <TextPanel c={c} label="Befund">
                <div
                  ref={befundPreviewRef}
                  onMouseUp={() => updateSelectionFrom("befund")}
                  style={{ userSelect: "text" }}
                >
                  <RichTextEditor
                    value={previewDraft?.befund || ""}
                    onChange={(value) => setPreviewDraft((prev) => ({ ...prev, befund: value }))}
                    minHeight="52vh"
                    c={c}
                    dark={dark}
                    ariaLabel={`${itemLabel} Befund bearbeiten`}
                  />
                </div>
              </TextPanel>

              <TextPanel c={c} label="Beurteilung">
                <div
                  ref={beurteilungPreviewRef}
                  onMouseUp={() => updateSelectionFrom("beurteilung")}
                  style={{ userSelect: "text" }}
                >
                  <RichTextEditor
                    value={previewDraft?.beurteilung || ""}
                    onChange={(value) => setPreviewDraft((prev) => ({ ...prev, beurteilung: value }))}
                    minHeight="22vh"
                    c={c}
                    dark={dark}
                    ariaLabel={`${itemLabel} Beurteilung bearbeiten`}
                  />
                </div>
              </TextPanel>
            </div>

            <div style={actionFooterStyle}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <AppButton c={c} dark={dark} tone="warning" onClick={() => copyToClipboard(currentPreview?.befund || "")} disabled={!hasRichTextContent(currentPreview?.befund)}>Befund kopieren</AppButton>
                <AppButton c={c} dark={dark} tone="warning" onClick={() => copyToClipboard(currentPreview?.beurteilung || "")} disabled={!hasRichTextContent(currentPreview?.beurteilung)}>Beurteilung kopieren</AppButton>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <AppButton c={c} dark={dark} onClick={applySelectedText} disabled={!selectedPreviewText}>Auswahl übernehmen</AppButton>
                <AppButton c={c} dark={dark} tone="info" onClick={() => { loadBefundbeispiel(currentPreview, "befund"); setPreviewTpl(null); }} disabled={!hasRichTextContent(currentPreview?.befund)}>Nur Befund übernehmen</AppButton>
                <AppButton c={c} dark={dark} tone="info" onClick={() => { loadBefundbeispiel(currentPreview, "beurteilung"); setPreviewTpl(null); }} disabled={!hasRichTextContent(currentPreview?.beurteilung)}>Nur Beurteilung übernehmen</AppButton>
                <AppButton c={c} dark={dark} tone="primary" onClick={() => { loadBefundbeispiel(currentPreview, "all"); setPreviewTpl(null); }}>Übernehmen</AppButton>
                <AppButton c={c} dark={dark} tone="danger" onClick={requestClosePreview}>Schließen</AppButton>
              </div>
            </div>
        </aside>
      )}
      </div>

      {isCreateSectionModalOpen && (
        <div
          onClick={() => setIsCreateSectionModalOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(520px, 95vw)",
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Neue Modalität/Sektion anlegen</div>
            <input
              type="text"
              value={newBefundGroupName}
              onChange={(e) => setNewBefundGroupName(e.target.value)}
              placeholder="Modalität/Sektion eingeben (z. B. PET)..."
              style={{ ...inp, width: "100%" }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  void confirmCreateSectionModal();
                }
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setIsCreateSectionModalOpen(false)}
                style={{
                  background: dark ? "#2d1515" : "#fff0f0",
                  color: dark ? "#fc8181" : "#cc3333",
                  border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  padding: "6px 12px",
                  fontSize: 12,
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void confirmCreateSectionModal()}
                style={{
                  background: dark ? "#0d2d1a" : "#f0fff4",
                  color: dark ? "#48bb78" : "#276749",
                  border: `1px solid ${dark ? "#48bb78" : "#9ae6b4"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(BefundbeispieleTab);
